import { api, ApiError } from "../api";
import { errorBanner, loadingBlock } from "./shared/blocks";
import { createLifecycle, isAbort } from "./shared/lifecycle";
import { h, tidy } from "./shared/safe";
import { clientWorkspaceNav } from "./client/workspaceNav";

type Entry = { id?: unknown; actor?: unknown; action?: unknown; entityType?: unknown; entityId?: unknown; createdAt?: unknown };

export async function clientActivityView(root: HTMLElement, params: Record<string, string>): Promise<void> {
  const id = typeof params.id === "string" ? params.id : "";
  const page = h("section", { class: "qf cp-client-activity" });
  root.replaceChildren(page);
  if (!id) return void page.append(errorBanner("This page needs a client. Go back to Clients and open a workspace."));
  const life = createLifecycle(page);
  page.append(loadingBlock("Loading client activity", 4));
  try {
    const [clientReply, activityReply] = await Promise.all([
      api<unknown>(`/v1/clients/${encodeURIComponent(id)}`),
      api<{ entries?: Entry[]; page?: { total?: number } }>(`/v1/audit?clientId=${encodeURIComponent(id)}&limit=50&offset=0`),
    ]);
    if (!life.alive()) return;
    const record = clientReply && typeof clientReply === "object" ? (clientReply as Record<string, unknown>).client : null;
    const name = record && typeof record === "object" && typeof (record as Record<string, unknown>).business_name === "string" ? tidy((record as Record<string, unknown>).business_name, 180) : "Client";
    const cid = encodeURIComponent(id);
    const nav = clientWorkspaceNav(id, "Activity", name);
    const entries = Array.isArray(activityReply.entries) ? activityReply.entries : [];
    const list = entries.length ? h("ol", { class: "cp-ca-list" }, ...entries.map((entry) => {
      const when = typeof entry.createdAt === "number" ? new Intl.DateTimeFormat(undefined, { dateStyle: "medium", timeStyle: "short" }).format(new Date(entry.createdAt * 1000)) : "Time unavailable";
      return h("li", null, h("span", { class: "cp-ca-dot", "aria-hidden": "true" }), h("div", null, h("strong", null, tidy(entry.action, 120)), h("p", null, `${tidy(entry.entityType ?? "Workspace", 80)}${entry.entityId ? ` · ${tidy(entry.entityId, 120)}` : ""}`)), h("time", null, when), h("span", { class: "cp-ca-actor" }, tidy(entry.actor ?? "Owner", 80)));
    })) : h("div", { class: "cp-cw-empty" }, h("strong", null, "No activity recorded yet"), h("p", null, "Changes and actions for this client will appear here when they are recorded."));
    page.replaceChildren(
      h("div", { class: "cp-cw-heading" }, h("div", null, h("a", { class: "cp-cw-back", href: "#/clients" }, "← Clients"), h("p", { class: "cp-cw-eyebrow" }, "Client workspace · Activity"), h("h1", null, name), h("p", { class: "cp-cw-subtitle" }, `${activityReply.page?.total ?? entries.length} recorded events`))),
      nav,
      h("section", { class: "cp-ca-panel" }, h("h2", null, "Recent activity"), h("p", { class: "cp-ca-note" }, "Activity is shown in the order it was recorded."), list),
    );
  } catch (error) {
    if (!life.alive() || isAbort(error)) return;
    page.replaceChildren(errorBanner(error instanceof ApiError ? `Client activity could not be loaded (HTTP ${error.status}).` : "Client activity could not be loaded.", { retry: () => void clientActivityView(root, params) }));
  }
}
